import React from 'react';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageMeta from '../components/PageMeta';

const PrivacyPolicy = () => {
  return (
    <div className="bg-black min-h-screen pt-32 md:pt-48">
      <PageMeta 
        title="Privacy Policy | Living Media Video Editing Services"
        description="Read how Living Media collects and protects your data. Clear details on privacy for our video editing clients and website visitors."
      />
      <Header />
      <main className="max-w-[1400px] mx-auto px-6 pb-24 md:pb-32">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-[2px] bg-[#BEF264]" />
            <span className="text-[#BEF264] font-black uppercase tracking-[0.4em] text-[10px]">
              Legal Documentation
            </span>
          </div>
          <h1 className="text-5xl md:text-8xl font-black text-white tracking-tighter leading-none mb-16 uppercase italic">
            PRIVACY <br /><span className="not-italic text-[#BEF264]">POLICY.</span>
          </h1>

          <div className="grid lg:grid-cols-12 gap-12 border-t border-white/10 pt-16">
            <aside className="lg:col-span-4">
              <div className="sticky top-32 space-y-4">
                <p className="text-white/40 font-black uppercase tracking-widest text-[10px]">Last updated</p>
                <p className="text-white font-medium">January 2026</p>
                <div className="h-px bg-white/5 w-full my-8" />
                <p className="text-white/40 font-black uppercase tracking-widest text-[10px]">Entity</p>
                <p className="text-[#BEF264] font-medium italic">Living Media</p>
              </div>
            </aside>

            <div className="lg:col-span-8 space-y-12">
              <section className="space-y-6">
                <p className="text-white/70 leading-relaxed text-lg font-medium tracking-tight">
                  Living Media (“we”, “our”, “us”) respects your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, and protect information when you visit our website or engage with our services.
                </p>
              </section>

              <section className="space-y-6">
                <h2 className="text-2xl font-black text-white uppercase tracking-tight italic">Information We Collect</h2>
                <p className="text-white/70 leading-relaxed text-lg font-medium tracking-tight">
                  We may collect the following information:
                </p>
                <ul className="space-y-4 text-white/70 text-lg font-medium tracking-tight">
                  <li className="flex gap-4"><span className="text-[#BEF264]">•</span> Name, email address, phone number, and company details</li>
                  <li className="flex gap-4"><span className="text-[#BEF264]">•</span> Information submitted via contact forms or enquiries</li>
                  <li className="flex gap-4"><span className="text-[#BEF264]">•</span> Communication history (emails, messages, calls)</li>
                  <li className="flex gap-4"><span className="text-[#BEF264]">•</span> Website usage data (analytics, cookies, IP address)</li>
                </ul>
              </section>

              <section className="space-y-6">
                <h2 className="text-2xl font-black text-white uppercase tracking-tight italic">How We Use Your Information</h2>
                <p className="text-white/70 leading-relaxed text-lg font-medium tracking-tight">
                  We use your information to:
                </p>
                <ul className="space-y-4 text-white/70 text-lg font-medium tracking-tight">
                  <li className="flex gap-4"><span className="text-[#BEF264]">•</span> Respond to enquiries and provide our services</li>
                  <li className="flex gap-4"><span className="text-[#BEF264]">•</span> Communicate about projects, trials, or ongoing work</li>
                  <li className="flex gap-4"><span className="text-[#BEF264]">•</span> Improve our website and services</li>
                  <li className="flex gap-4"><span className="text-[#BEF264]">•</span> Send relevant updates or information (you can opt out at any time)</li>
                </ul>
                <p className="text-white/70 leading-relaxed text-lg font-medium tracking-tight">
                  We do not sell or rent your data to third parties.
                </p>
              </section>

              <section className="space-y-6">
                <h2 className="text-2xl font-black text-white uppercase tracking-tight italic">Video Content & Confidentiality</h2>
                <p className="text-white/70 leading-relaxed text-lg font-medium tracking-tight">
                  Any video files, assets, or materials shared with Living Media remain the property of the client. We treat all content as confidential unless explicit permission is given to showcase work publicly.
                </p>
              </section>

              <section className="space-y-6">
                <h2 className="text-2xl font-black text-white uppercase tracking-tight italic">Data Storage & Security</h2>
                <p className="text-white/70 leading-relaxed text-lg font-medium tracking-tight">
                  We take reasonable measures to protect your data from unauthorised access, disclosure, or misuse. Data is stored securely and only accessed when necessary to deliver our services.
                </p>
              </section>

              <section className="space-y-6">
                <h2 className="text-2xl font-black text-white uppercase tracking-tight italic">Cookies</h2>
                <p className="text-white/70 leading-relaxed text-lg font-medium tracking-tight">
                  Our website may use cookies or similar technologies to improve user experience and analyse site performance. You can control cookie settings through your browser.
                </p>
              </section>

              <section className="space-y-6">
                <h2 className="text-2xl font-black text-white uppercase tracking-tight italic">Your Rights</h2>
                <p className="text-white/70 leading-relaxed text-lg font-medium tracking-tight">
                  You have the right to:
                </p>
                <ul className="space-y-4 text-white/70 text-lg font-medium tracking-tight">
                  <li className="flex gap-4"><span className="text-[#BEF264]">•</span> Request access to your data</li>
                  <li className="flex gap-4"><span className="text-[#BEF264]">•</span> Request corrections or deletion</li>
                  <li className="flex gap-4"><span className="text-[#BEF264]">•</span> Withdraw consent at any time</li>
                </ul>
                <p className="text-white/70 leading-relaxed text-lg font-medium tracking-tight">
                  To exercise these rights, contact us directly.
                </p>
              </section>

              <section className="space-y-6">
                <h2 className="text-2xl font-black text-white uppercase tracking-tight italic">Contact</h2>
                <p className="text-white/70 leading-relaxed text-lg font-medium tracking-tight">
                  If you have any questions about this Privacy Policy, please contact Living Media via the details provided on our website.
                </p>
              </section>
            </div>
          </div>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;